import React, { memo, useEffect, useState } from "react";
import styles from "./support-chat-modal.module.scss";
import caretDown from "@src/assets/svg/caret-down.svg";
import companyIcon from "@src/assets/svg/company-icon-gray.svg";
import ticketIdIcon from "@src/assets/svg/ticket-id-icon.svg";
import { useI18N } from "@src/language/language-context";
import cn from "classnames";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import AutoTranslationButton from "@src/components/atoms/auto-translation-button/auto-translation-button";
import { SourceSelectionSection } from "@src/components/molecules/source-selection-section/source-selection-section";
import InitialsAvatar from "@src/components/atoms/initials-avatar/initials-avatar";
import { supportTicketByIdType } from "@src/types/support";
import apiUseCases from "@src/cloud-connect/api";
import store from "@src/reducers/store";

const cssClassName = "support-chat-modal";

interface SupportChatTicketCardProps {
  ticket: supportTicketByIdType;
  isBot: boolean;
  isMachineSaveDisabled?: boolean;
  onAutoTranslationToggle?: () => void;
  onMachineAssignment?: () => void;
}

export const SupportChatTicketCard = memo(
  ({
    ticket,
    isBot,
    isMachineSaveDisabled = false,
    onAutoTranslationToggle,
    onMachineAssignment,
  }: SupportChatTicketCardProps) => {
    const i18n = useI18N();
    const [isTicketExpanded, setIsTicketExpanded] = useState(true);
    void isBot;

    const texts = {
      issueDetails: i18n("modules.supportChat.issueDetails"),
      sourceLabel: i18n("modules.supportChat.sourceLabel"),
      sourcePlaceholder: i18n("modules.supportChat.sourcePlaceholder"),
      noSourcesFound: i18n("modules.supportChat.noSourcesFound"),
      saveButton: i18n("modules.supportChat.saveButton"),
    };

    const machineSourceId = ticket.machine?.machine_id ?? null;

    // Resolve the machine name the same way the /tickets page's selection component does:
    // match the stored source id against the namespace's sources list; fall back to the
    // raw id (the backend machine_name may be null when the lookup fails server-side).
    const [resolvedMachine, setResolvedMachine] = useState<{
      sourceId: string;
      name: string;
    } | null>(null);
    useEffect(() => {
      if (!machineSourceId) return;
      let cancelled = false;
      (async () => {
        try {
          const namespace =
            store.getState().yourSlice.authContext.customerNamespace;
          const response = await apiUseCases.getAllSourcesList(namespace);
          if (cancelled) return;
          const match = Object.values(response.data_sources).find(
            (source) => source.source_id === machineSourceId,
          );
          setResolvedMachine({
            sourceId: machineSourceId,
            name: match?.name || machineSourceId,
          });
        } catch {
          if (!cancelled) {
            setResolvedMachine({
              sourceId: machineSourceId,
              name: machineSourceId,
            });
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [machineSourceId]);

    const machineName = machineSourceId
      ? resolvedMachine?.sourceId === machineSourceId
        ? resolvedMachine.name
        : ticket.machine?.machine_name || machineSourceId
      : null;

  const companyName = ticket?.org_name || null;

    return (
      <div className={styles[`${cssClassName}__ticket-card-panel-details`]}>
        <div className={styles[`${cssClassName}__ticket-card`]}>
          <div className={styles[`${cssClassName}__ticket-title-row`]}>
            {/* <IndicatorText text="" variant={"green"} /> */}
            {machineName ? (
              <div className={styles[`${cssClassName}__machine-title-wrapper`]}>
                <p className={styles[`${cssClassName}__machine-title-label`]}>
                  {texts.sourceLabel}
                </p>
                <h3 className={styles[`${cssClassName}__ticket-title`]}>
                  {machineName}
                </h3>
              </div>
            ) : (
              <div
                className={styles[`${cssClassName}__machine-selection-column`]}
              >
                {/* show an empty, selectable dropdown; never pre-fill it from the ticket's
                    machine reference - the name is either on top or unknown */}
                <SourceSelectionSection
                  ticketId={ticket.id}
                  ticketSourceId={null}
                  callbackForRefresh={onMachineAssignment}
                  isSaveDisabled={isMachineSaveDisabled}
                  sourceLabel={texts.sourceLabel}
                  sourcePlaceholder={texts.sourcePlaceholder}
                  noSourcesFound={texts.noSourcesFound}
                  confirmButton={texts.saveButton}
                />
              </div>
            )}
            <div className={styles[`${cssClassName}__collapse-button-wrapper`]}>
              <button
                type="button"
                className={cn(
                  styles[`${cssClassName}__collapse-button`],
                  !isTicketExpanded &&
                    styles[`${cssClassName}__collapse-button--collapsed`],
                )}
                onClick={() => setIsTicketExpanded((prev) => !prev)}
              >
                <img
                  src={caretDown}
                  alt="Collapse details"
                  className={cn(
                    styles[`${cssClassName}__collapse-icon`],
                    styles[`${cssClassName}__collapse-icon--top`],
                  )}
                />
                <img
                  src={caretDown}
                  alt="Expand details"
                  className={cn(
                    styles[`${cssClassName}__collapse-icon`],
                    styles[`${cssClassName}__collapse-icon--bottom`],
                  )}
                />
              </button>
            </div>
          </div>

          {isTicketExpanded && (
            <div className={styles[`${cssClassName}__ticket-company`]}>
              <img
                src={companyIcon}
                alt="Company icon"
                className={styles[`${cssClassName}__ticket-company-icon`]}
              />
              <p>{companyName}</p>
            </div>
          )}

          <div className={styles[`${cssClassName}__meta-row`]}>
            <span className={styles[`${cssClassName}__meta-pill`]}>
              <img
                src={ticketIdIcon}
                alt="Ticket ID icon"
                className={styles[`${cssClassName}__meta-pill-icon`]}
              />
              {ticket.id}
            </span>
            <span
              className={cn(
                styles[`${cssClassName}__meta-pill`],
                ticket.status &&
                  styles[
                    `${cssClassName}__meta-pill--${ticket.status.toLowerCase()}`
                  ],
              )}
            >
              {ticket.status ? ticket.status.toUpperCase() : ""}
            </span>
          </div>
          <FlexRowContainer justifyContent="space-between">
            {ticket?.raised_by?.name && (
              <div className={styles[`${cssClassName}__agent-info`]}>
                <InitialsAvatar name={ticket.raised_by.name} />
                <span className={styles[`${cssClassName}__agent-name`]}>
                  {ticket.raised_by.name}
                </span>
              </div>
            )}
            <AutoTranslationButton
              key={ticket.id}
              ticketId={ticket.id}
              disabled={isBot}
              backgroundColor="background"
              ticketAutoTranslationActive={ticket.auto_translate}
              callbackForRefresh={onAutoTranslationToggle}
            />
          </FlexRowContainer>
        </div>
        {isTicketExpanded && (
          <FlexRowContainer justifyContent="space-between">
            <div className={styles[`${cssClassName}__issue-content`]}>
              <h4 className={styles[`${cssClassName}__issue-title`]}>
                {ticket.title}
              </h4>
              <div className={styles[`${cssClassName}__issue-details`]}>
                <p className={styles[`${cssClassName}__issue-details-label`]}>
                  {texts.issueDetails}
                </p>
                <p className={styles[`${cssClassName}__issue-details-text`]}>
                  {ticket.description}
                </p>
              </div>
            </div>
          </FlexRowContainer>
        )}
      </div>
    );
  },
);

SupportChatTicketCard.displayName = "SupportChatTicketCard";
